import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { StyledAnchor } from "@/components/common/styles";
import { newTabProps } from "@/components/marketing/marketingLinks";
import {
  BODY,
  BODY_SMALL,
  HEADING_2,
} from "@/components/marketing/marketingType";
import {
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { GroupLayout, Laid } from "@/content/landing/sectionLayout";
import {
  LandingImage,
  LandingSection,
  SectionHeading,
} from "../LandingPrimitives";
import { Lines } from "@/components/marketing/Lines";
import { sectionPad } from "./landingRun";

/** `.integr_categ_visual` stops here once the category stacks. */
const CATEGORY_VISUAL_CAP = "609.71px";

type LinkGrid = Laid<"linkGrid">;
type Group = LinkGrid["groups"][number];

/**
 * `body_new.opacity-70`, the lead these two bands print, which is not the
 * small one: `.body_new`'s own ladder and left-set lines, held at 70%.
 */
const GROUP_LEAD = [BODY, { opacity: 0.7 }];

/** `.loc-resi_toptst_wrap` sets the grey the panel's lead inherits. */
const PANEL_LEAD = [...GROUP_LEAD, { color: marketingPalette.inkMuted }];

/**
 * `loc-resi_wrap`: the locations band, heading and all, on its own tinted
 * panel. The panel is what sets the grid's measure, so the cards come out
 * 306px at 1440 and 159px on a phone without either width being written down.
 *
 * The card is the flag in a bordered well, the country, then the count and its
 * unit side by side. The unit is its own element because the published one is:
 * run together it reads "6,652,539IPs", which is what the parity harness was
 * reporting as a changed block on all four product pages.
 */
function Tiles({
  group,
  bodyTone,
  narrow,
}: {
  group: Group;
  bodyTone?: "small";
  narrow?: boolean;
}) {
  return (
    <Box
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          p: "40px",
          borderRadius: "32px",
          backgroundColor: marketingPalette.surface,
        },
        { [TINY]: { p: "24px 16px", borderRadius: "16px" } },
      ]}
    >
      <SectionHeading
        bodyTone={bodyTone}
        title={group.title}
        body={group.body}
        gap="9.6px"
        align="center"
        {...(narrow ? { measure: "50ch" } : {})}
      />
      <Box
        sx={[
          {
            display: "grid",
            gap: "8px",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          },
          { [NOT_WIDE]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" } },
        ]}
      >
        {group.items.map((item) => (
          <StyledAnchor
            key={item.href}
            href={item.href}
            {...newTabProps(item)}
            sx={[
              {
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                p: "16px",
                borderRadius: "12px",
                border: `1px solid ${marketingPalette.wash}`,
                backgroundColor: "common.white",
                boxShadow: "rgb(220, 222, 224) 0px 8px 24px -11px",
                "&:hover": { borderColor: marketingPalette.inkSubtle },
              },
              { [TINY]: { gap: "6.4px", p: "9.6px 16px" } },
            ]}
          >
            {item.icon ? (
              <Box
                sx={[
                  {
                    p: "6.4px",
                    borderRadius: "8px",
                    border: `1px solid ${marketingPalette.wash}`,
                    alignSelf: "flex-start",
                    lineHeight: 0,
                  },
                  // Half a phone wide, the flag loses its well and stands alone.
                  { [TINY]: { p: 0, border: "none" } },
                ]}
              >
                <LandingImage
                  src={item.icon}
                  sx={[
                    {
                      height: "33.6px",
                      width: "33.6px",
                      objectFit: "contain",
                    },
                    { [TINY]: { height: "28.8px", width: "28.8px" } },
                  ]}
                />
              </Box>
            ) : null}
            <Box
              sx={[
                { fontSize: 22.4, fontWeight: 500, lineHeight: "33.6px" },
                { [TINY]: { fontSize: 14.4, lineHeight: "21.6px" } },
              ]}
            >
              {item.title}
            </Box>
            {item.note ? (
              <Box
                sx={[
                  { display: "flex", gap: "3px", lineHeight: "22.4px" },
                  { [TINY]: { fontSize: 13.6, lineHeight: "19.04px" } },
                ]}
              >
                <Box>{item.note}</Box>
                {item.noteUnit ? <Box>{item.noteUnit}</Box> : null}
              </Box>
            ) : null}
          </StyledAnchor>
        ))}
      </Box>
    </Box>
  );
}

/** `home-loc_item` as the popular-integrations band draws it: icon over name. */
function PanelGroup({ group }: { group: Group }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        p: "40px",
        borderRadius: "32px",
        backgroundColor: marketingPalette.surface,
        [TINY]: { p: "24px 16px", borderRadius: "16px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "9.6px",
          alignItems: "center",
          textAlign: "center",
          maxWidth: "612px",
          mx: "auto",
        }}
      >
        <Typography component="h2" sx={HEADING_2}>
          <Lines text={group.title} />
        </Typography>
        {group.body ? (
          <Typography sx={PANEL_LEAD}>{group.body}</Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: "8px",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          [NOT_WIDE]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        {group.items.map((item) => (
          <StyledAnchor
            key={item.href}
            href={item.href}
            {...newTabProps(item)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "12px",
              p: "16px",
              borderRadius: "12px",
              border: `1px solid ${marketingPalette.wash}`,
              backgroundColor: "common.white",
              "&:hover": { borderColor: marketingPalette.inkSubtle },
              // `.home-loc_item` on a phone: 6.4 between its parts, 9.6 over and under.
              [TINY]: { gap: "6.4px", py: "9.6px" },
            }}
          >
            {item.icon ? (
              <LandingImage
                src={item.icon}
                sx={{
                  height: "32px",
                  width: "32px",
                  objectFit: "contain",
                  [TINY]: { height: "28.8px", width: "28.8px" },
                }}
              />
            ) : null}
            <Box
              sx={{
                fontSize: 19.04,
                fontWeight: 500,
                lineHeight: "28.56px",
                [TINY]: { fontSize: 14.4, lineHeight: "21.6px" },
              }}
            >
              {item.title}
            </Box>
          </StyledAnchor>
        ))}
      </Box>
    </Box>
  );
}

/**
 * `integr_categ_wrap`, a category of integrations beside its illustration.
 *
 * The picture is what fixes the band's height: the published band is 405px tall
 * whether it lists two tools or twenty, and the chips wrap inside half the
 * frame rather than running the full width. Drawn as a full-width grid instead,
 * the two-tool band came out 894px short of the published one.
 */
function CategoryGroup({
  group,
  reversed,
}: {
  group: Group;
  reversed: boolean;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        // `.integr_categ_wrap`: as many 480 columns as the row fits, which is
        // two at the wide step and one at 1000, with no breakpoint between.
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 480px), 1fr))",
        gap: "64px",
        alignItems: "center",
        [NOT_WIDE]: { gap: "32px" },
        [TINY]: { display: "flex", flexDirection: "column" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "16px",
          // `.is-opposite` publishes its art first, which the one-column grid
          // keeps; only the phone's `column-reverse` brings the words up.
          order: reversed ? 2 : 1,
          [TINY]: { order: 1 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            maxWidth: "498px",
          }}
        >
          <Typography component="h2" sx={HEADING_2}>
            <Lines text={group.title} />
          </Typography>
          {group.body ? (
            <Typography sx={GROUP_LEAD}>{group.body}</Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8.8px",
          }}
        >
          {group.items.map((item) => (
            <StyledAnchor
              key={item.href}
              href={item.href}
              {...newTabProps(item)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4.8px",
                p: "4.8px 6.4px",
                borderRadius: "8px",
                border: `1px solid ${marketingPalette.outline}`,
                backgroundColor: marketingPalette.surface,
                fontSize: 13,
                lineHeight: "19.5px",
              }}
            >
              {item.icon ? (
                <LandingImage
                  src={item.icon}
                  sx={{ height: "20px", width: "20px", objectFit: "contain" }}
                />
              ) : null}
              <Box component="span">{item.title}</Box>
            </StyledAnchor>
          ))}
        </Box>
      </Box>
      {group.image ? (
        <LandingImage
          src={group.image}
          sx={{
            width: 1,
            aspectRatio: "632 / 405",
            objectFit: "cover",
            borderRadius: "8px",
            order: reversed ? 1 : 2,
            // Stacked, the published illustration stops growing at this width
            // rather than filling the column, measured constant at 991, 768
            // and 480, so it is a cap and not a step.
            [NOT_WIDE]: { maxWidth: CATEGORY_VISUAL_CAP },
            [TINY]: { order: 2 },
          }}
        />
      ) : null}
    </Box>
  );
}

/** Location tiles, popular integrations, and the four integration categories. */
export default function LinkGridBlock({ block }: { block: LinkGrid }) {
  return (
    <LandingSection padding={sectionPad(block.pad)}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "40px" }}>
        {block.groups.map((group, index) => {
          const layout: GroupLayout = block.groupLayout?.[index] ?? {};
          if (group.variant === "panel") {
            return <PanelGroup key={index} group={group} />;
          }
          if (group.variant === "category") {
            return (
              <CategoryGroup
                key={index}
                group={group}
                reversed={layout.reversed === true}
              />
            );
          }
          if (group.variant === "tile") {
            return (
              <Tiles
                key={index}
                group={group}
                bodyTone={layout.bodyTone ?? block.bodyTone}
                narrow={layout.narrowHead}
              />
            );
          }
          return (
            <Box
              key={index}
              sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <SectionHeading
                bodyTone={layout.bodyTone ?? block.bodyTone}
                title={group.title}
                body={group.body}
                align="left"
              />
              <Box
                sx={{
                  display: "grid",
                  gap: "12px",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  [NOT_WIDE]: {
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  },
                  [TINY]: { gridTemplateColumns: "1fr" },
                }}
              >
                {group.items.map((item, itemIndex) => (
                  <StyledAnchor
                    key={itemIndex}
                    href={item.href}
                    {...newTabProps(item)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      p: "12px 16px",
                      borderRadius: "12px",
                      border: `1px solid ${marketingPalette.hairline}`,
                      "&:hover": {
                        backgroundColor: marketingPalette.surface,
                      },
                    }}
                  >
                    {item.icon ? (
                      <LandingImage
                        src={item.icon}
                        sx={{
                          height: "24px",
                          width: "24px",
                          objectFit: "contain",
                        }}
                      />
                    ) : null}
                    <Box>
                      <Box sx={{ fontSize: 15 }}>{item.title}</Box>
                      {item.note ? (
                        <Box sx={BODY_SMALL}>{item.note}</Box>
                      ) : null}
                    </Box>
                  </StyledAnchor>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </LandingSection>
  );
}
